import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, BarChart3, Radar } from "lucide-react";

import { PageTemplate } from "@components/pageTemplate/PageTemplate";
import { usePesTestLazyQuery } from "generated/hooks";
import { Skeleton } from "~/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";

import { ComparisonRadarChart } from "./comparison/ComparisonRadarChart";
import { ActualValuesCharts } from "./comparison/ActualValuesCharts";
import {
  filterAndSortResults,
  TestWithResults,
  IndicatorResult,
  decodeTestIds,
} from "./comparison/utils";

const CompareTests: React.FC = () => {
  const [searchParams] = useSearchParams();
  const testIds = decodeTestIds(searchParams.get("tests") ?? "");

  const [fetchPesTest] = usePesTestLazyQuery();
  const [tests, setTests] = useState<TestWithResults[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (testIds.length === 0) {
      setLoading(false);
      return;
    }

    const fetchAllTests = async () => {
      setLoading(true);
      setError(null);

      try {
        const fetchedTests = await Promise.all(
          testIds.map(async (id): Promise<TestWithResults | null> => {
            const { data, error: queryError } = await fetchPesTest({
              variables: { id },
            });

            if (queryError) {
              console.error(`Error fetching test ${id}:`, queryError);
              return null;
            }

            const pesTest = data?.pesTest;
            if (!pesTest) {
              return null;
            }

            return {
              id: pesTest.id,
              name: pesTest.name,
              originalModelName: pesTest.originalModelName,
              results: pesTest.results?.nodes as unknown as IndicatorResult[],
            };
          })
        );

        const validTests = fetchedTests.filter(
          (test): test is TestWithResults => test !== null
        );

        if (validTests.length === 0) {
          setError("Could not load any of the selected tests.");
        }

        setTests(validTests);
      } catch (err) {
        console.error("Error fetching tests:", err);
        setError("An error occurred while loading tests.");
      } finally {
        setLoading(false);
      }
    };

    fetchAllTests();
  }, [testIds.join(","), fetchPesTest]);

  const testsWithResults = tests.filter(
    (test) => filterAndSortResults(test.results, test.originalModelName).length > 0
  );

  const testsWithoutResults = tests.filter(
    (test) => filterAndSortResults(test.results, test.originalModelName).length === 0
  );

  if (testIds.length === 0) {
    return (
      <PageTemplate
        title="Compare Tests"
        activeItem="My Tests"
        breadcrumbs={[
          { label: "My Tests", path: "/dashboard" },
          { label: "Compare Tests", path: "/compare" },
        ]}
      >
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>No tests selected</AlertTitle>
          <AlertDescription>
            Please select at least 2 tests from the My Tests page to compare.
          </AlertDescription>
        </Alert>
      </PageTemplate>
    );
  }

  if (loading) {
    return (
      <PageTemplate
        title="Compare Tests"
        activeItem="My Tests"
        breadcrumbs={[
          { label: "My Tests", path: "/dashboard" },
          { label: "Compare Tests", path: "/compare" },
        ]}
      >
        <div className="space-y-4">
          <Skeleton className="h-8 w-1/3" />
          <Skeleton className="h-4 w-1/4" />
          <div className="grid gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-64 w-full" />
            ))}
          </div>
        </div>
      </PageTemplate>
    );
  }

  if (error) {
    return (
      <PageTemplate
        title="Compare Tests"
        activeItem="My Tests"
        breadcrumbs={[
          { label: "My Tests", path: "/dashboard" },
          { label: "Compare Tests", path: "/compare" },
        ]}
      >
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate
      title="Compare Tests"
      activeItem="My Tests"
      breadcrumbs={[
        { label: "My Tests", path: "/dashboard" },
        { label: "Compare Tests", path: "/compare" },
      ]}
    >
      <div className="space-y-6">
        {testsWithResults.length < 2 ? (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Not enough data</AlertTitle>
            <AlertDescription>
              At least 2 tests with results are required for comparison.
              {testsWithoutResults.length > 0 &&
                " Some of the selected tests are still awaiting processing."}
            </AlertDescription>
          </Alert>
        ) : (
          <Tabs defaultValue="bar" className="w-full">
            <TabsList className="grid w-full max-w-md grid-cols-2">
              <TabsTrigger value="bar" className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Bar
              </TabsTrigger>
              <TabsTrigger value="radar" className="flex items-center gap-2">
                <Radar className="h-4 w-4" />
                Radar
              </TabsTrigger>
            </TabsList>
            <TabsContent value="radar" className="mt-6">
              <ComparisonRadarChart tests={testsWithResults} />
            </TabsContent>
            <TabsContent value="bar" className="mt-6">
              <ActualValuesCharts tests={testsWithResults} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </PageTemplate>
  );
};

export default CompareTests;
